import { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPhone } from "@fortawesome/free-solid-svg-icons";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Dialog, DialogOverlay, DialogPortal } from "./ui/dialog";
import { sendInvestmentOtp, submitInvestmentRequirement } from "../services/ventureApi";

const EMPTY_FORM = {
  name: "",
  mobile: "",
  intent: ""
};

const getSubmissionErrorMessage = (error) => {
  const responseData = error?.response?.data;
  const serverMessage = typeof responseData === "string"
    ? responseData
    : responseData?.message || responseData?.Message || responseData?.errorMessage || responseData?.ErrorMessage || responseData?.title;

  if (typeof serverMessage === "string" && serverMessage.trim()) {
    return serverMessage;
  }

  if (error?.response?.status) {
    return `Requirement service returned HTTP ${error.response.status}. Please try again.`;
  }

  if (error?.code === "ECONNABORTED") {
    return "The requirement service timed out. Please try again.";
  }

  return "Could not connect to the requirement service. Please check your connection and try again.";
};

const getSiteConfig = () => {
  const hostname = window.location.hostname.toLowerCase();

  if (hostname.includes("rrrhyderabad.com")) {
    return { name: "RRR Hyderabad", url: "RRRHyderabad.com" };
  }

  return { name: "Future City Hyderabad", url: "FutureCityOfficial.com" };
};

function InvestmentModal({ open, onOpenChange }) {
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [stage, setStage] = useState("form");
  const [otp, setOtp] = useState("");
  const [generatedOtp, setGeneratedOtp] = useState("");
  const [isMobileVerified, setIsMobileVerified] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("");

  const closeModal = () => {
    setFormData(EMPTY_FORM);
    setStage("form");
    setOtp("");
    setGeneratedOtp("");
    setIsMobileVerified(false);
    setIsSubmitting(false);
    setError("");
    setStatus("");
    onOpenChange(false);
  };

  const handleOpenChange = (nextOpen) => {
    if (!nextOpen) {
      closeModal();
    } else {
      onOpenChange(true);
    }
  };

  const handleFormChange = (event) => {
    const { name, value } = event.target;
    setFormData((previous) => ({ ...previous, [name]: value }));
    setError("");
  };

  const handleRequestOtp = async (event) => {
    event.preventDefault();
    setError("");
    setStatus("");

    if (!event.currentTarget.reportValidity()) {
      return;
    }

    const nextOtp = String(Math.floor(100000 + Math.random() * 900000));
    setIsSubmitting(true);

    try {
      await sendInvestmentOtp({ otp: nextOtp, mobile: formData.mobile });
      setGeneratedOtp(nextOtp);
      setStage("otp");
    } catch (requestError) {
      setError(requestError?.response?.data?.message || "Unable to send an OTP. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerifyOtp = () => {
    if (!/^\d{6}$/.test(otp)) {
      setIsMobileVerified(false);
      setStatus("Enter the 6-digit OTP sent to your mobile.");
      return;
    }

    if (otp !== generatedOtp) {
      setIsMobileVerified(false);
      setStatus("Invalid OTP. Please try again.");
      return;
    }

    setIsMobileVerified(true);
    setStatus("Mobile verified successfully.");
    setError("");
  };

  const handleSubmitRequirement = async () => {
    if (!isMobileVerified) {
      setError("Please verify your mobile number first.");
      return;
    }

    setIsSubmitting(true);
    setError("");

    const siteConfig = getSiteConfig();
    const payload = {
      Name: formData.name,
      Mobile: formData.mobile,
      Email: "",
      Intent: formData.intent,
      PropertyType: "",
      Budget: "",
      Area: "",
      ProjectName: "",
      Message: "",
      SiteName: siteConfig.name,
      SiteUrl: siteConfig.url
    };

    try {
      const result = await submitInvestmentRequirement(payload);
      if (!result?.success) {
        setError("Unable to submit your requirement. Please try again.");
        return;
      }

      setStatus(`Thank you, ${formData.name}. Our team will contact you shortly at ${formData.mobile}.`);
      setStage("success");
    } catch (requestError) {
      setError(getSubmissionErrorMessage(requestError));
    } finally {
      setIsSubmitting(false);
    }
  };

  return <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogPortal>
        <DialogOverlay className="vd-investment-overlay" />
        <DialogPrimitive.Content
          id="investmentModal"
          className="vd-investment-modal"
          aria-describedby="investment-modal-description"
          onCloseAutoFocus={(event) => event.preventDefault()}
        >
          <button type="button" className="vd-investment-close" onClick={closeModal} aria-label="Close investment form">
            &times;
          </button>

          {stage === "form" && <>
              <DialogPrimitive.Title className="vd-investment-title">Get Best Deals</DialogPrimitive.Title>
              <DialogPrimitive.Description id="investment-modal-description" className="vd-investment-description">
                Our experts will call you within 30 minutes with the best options.
              </DialogPrimitive.Description>
              <form id="investmentForm" onSubmit={handleRequestOtp}>
                <div className="vd-investment-form-group">
                  <label htmlFor="name">Full Name</label>
                  <input id="name" name="name" type="text" placeholder="Enter your name" value={formData.name} onChange={handleFormChange} autoComplete="name" required />
                </div>
                <div className="vd-investment-form-group">
                  <label htmlFor="mobile">Mobile Number</label>
                  <input id="mobile" name="mobile" type="tel" placeholder="10-digit number" value={formData.mobile} onChange={handleFormChange} autoComplete="tel-national" inputMode="numeric" pattern="[0-9]{10}" maxLength={10} required />
                </div>
                <div className="vd-investment-form-group">
                  <label htmlFor="intent">I am looking for</label>
                  <select id="intent" name="intent" value={formData.intent} onChange={handleFormChange} required>
                    <option value="">Select...</option>
                    <option value="Residential Plot">Residential Plot</option>
                    <option value="Commercial Property">Commercial Property</option>
                    <option value="Farmland">Farmland</option>
                    <option value="Investment Advice">Investment Advice</option>
                  </select>
                </div>
                <button type="submit" className="vd-investment-submit" disabled={isSubmitting}>
                  <FontAwesomeIcon icon={faPhone} />
                  {isSubmitting ? "Sending OTP..." : "Get Expert Call"}
                </button>
              </form>
              <div className="vd-investment-contact">
                <span aria-hidden="true">💬</span>{" "}
                <a href="https://wa.me/918500667238">WhatsApp</a>
                {" | "}
                <span aria-hidden="true">📞</span>{" "}
                <a href="tel:9618641100">9618641100</a>
              </div>
            </>}

          {stage === "otp" && <>
              <DialogPrimitive.Title className="vd-investment-title">Verify Your Contact Details</DialogPrimitive.Title>
              <DialogPrimitive.Description id="investment-modal-description" className="vd-investment-description">
                Enter the 6-digit OTP sent to {formData.mobile}.
              </DialogPrimitive.Description>
              <div className="vd-investment-form-group">
                <label htmlFor="mobileOtp">Mobile OTP</label>
                <div className="vd-investment-otp-row">
                  <input id="mobileOtp" type="text" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}" maxLength={6} value={otp} onChange={(event) => {
                    setOtp(event.target.value.replace(/\D/g, "").slice(0, 6));
                    setStatus("");
                    setError("");
                    setIsMobileVerified(false);
                  }} disabled={isMobileVerified} />
                  <button type="button" className="vd-investment-verify" onClick={handleVerifyOtp} disabled={isMobileVerified}>Verify</button>
                </div>
              </div>
              {status && <p className={isMobileVerified ? "vd-investment-status is-success" : "vd-investment-status"} role="status">{status}</p>}
              <button type="button" className="vd-investment-submit" onClick={handleSubmitRequirement} disabled={!isMobileVerified || isSubmitting}>
                {isSubmitting ? "Sending..." : "Send Requirement"}
              </button>
            </>}

          {stage === "success" && <>
              <DialogPrimitive.Title className="vd-investment-title">Thank you for your interest!</DialogPrimitive.Title>
              <DialogPrimitive.Description id="investment-modal-description" className="vd-investment-description" role="status">
                {status}
              </DialogPrimitive.Description>
              <button type="button" className="vd-investment-submit" onClick={closeModal}>Done</button>
            </>}

          {error && <p className="vd-investment-error" role="alert">{error}</p>}
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>;
}

export default InvestmentModal;
